import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { httpClient } from '../utils/dataProvider';
import {
  buildActiveUserAnalytics,
  countActiveServices,
  countOpenTickets,
  groupReportLogs,
  recentActivity,
  summarizeBlocks,
  summarizeReportGroups,
} from './dashboardAnalytics';
import { fetchAllReportLogs } from './reportLogs';

const API = 'https://skilzapp.net/api';
const initialResource = { data: [], status: 'loading', error: null };
const number = value => Number(value || 0).toLocaleString();

const Icon = ({ type }) => {
  const paths = {
    users: 'M16 19v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V19M9.5 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7ZM16 8h5M18.5 5.5v5',
    services: 'm12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm-7.5 4.8 7.5 4.1 7.5-4.1',
    tickets: 'M5 5h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-7l-5 4v-4H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z',
    reports: 'M12 8v4m0 4h.01M10.3 4.2 2.7 18a2 2 0 0 0 1.75 3h15.1a2 2 0 0 0 1.75-3L13.7 4.2a2 2 0 0 0-3.4 0Z',
    send: 'm4 12 16-8-6.5 16-2.8-6.7L4 12Z',
    export: 'M12 3v12m0 0 4-4m-4 4-4-4M5 20h14',
  };
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d={paths[type] || paths.reports} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>;
};

const ResourceValue = ({ resource, children }) => {
  if (resource.status === 'loading') return <span className="sk-dashboard-loading">Loading…</span>;
  if (resource.status === 'error') return <span className="sk-dashboard-unavailable">Unavailable</span>;
  return children;
};

const MetricCard = ({ title, route, tone, icon, resource, value, detail }) => (
  <a className={`sk-dashboard-card-link sk-dashboard-metric ${tone}`} href={route}>
    <span className="sk-dashboard-metric-top">
      <span className="sk-dashboard-metric-label">{title}</span>
      <span className="sk-dashboard-metric-icon"><Icon type={icon} /></span>
    </span>
    <strong className="sk-dashboard-metric-value"><ResourceValue resource={resource}>{number(value)}</ResourceValue></strong>
    <span className="sk-dashboard-metric-detail">{resource.status === 'error' ? resource.error : detail}</span>
  </a>
);

const SectionHeading = ({ title, subtitle, children }) => (
  <div className="sk-dashboard-section-heading">
    <div><h2>{title}</h2><p>{subtitle}</p></div>
    {children}
  </div>
);

const ReportCard = ({ type, title, route, summary, status }) => {
  const fill = summary.total ? Math.round((summary.open / summary.total) * 100) : 0;
  return (
    <a className="sk-dashboard-card-link sk-dashboard-report-card" href={route}>
      <span className="sk-dashboard-report-title"><span className={`sk-dashboard-report-symbol ${type}`}><Icon type={type === 'users' ? 'users' : 'reports'} /></span><h3>{title}</h3></span>
      <span className="sk-dashboard-report-stats">
        <span className="sk-dashboard-report-count"><ResourceValue resource={status}>{number(summary.total)}</ResourceValue> <small>grouped reports</small></span>
        {status.status === 'success' && <span className="sk-dashboard-status-pill">{number(summary.open)} open</span>}
      </span>
      <span className="sk-dashboard-report-meter" aria-hidden="true"><span style={{ width: `${fill}%` }} /></span>
    </a>
  );
};

const ActiveChart = ({ buckets }) => {
  const width = 700;
  const top = 25;
  const bottom = 160;
  const max = Math.max(1, ...buckets.map(bucket => bucket.value));
  const points = buckets.map((bucket, index) => {
    const x = 20 + (buckets.length === 1 ? 0 : index * 660 / (buckets.length - 1));
    const y = bottom - (bucket.value / max) * (bottom - top);
    return { ...bucket, x, y };
  });
  const line = points.map((point, index) => `${index ? 'L' : 'M'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`).join(' ');
  const area = points.length ? `${line} L680 ${bottom} L20 ${bottom} Z` : '';
  const labelEvery = buckets.length <= 7 ? 1 : Math.ceil(buckets.length / 6);
  const dailyValues = buckets
    .map(bucket => `${bucket.key}: ${bucket.value} active user${bucket.value === 1 ? '' : 's'}`)
    .join('; ');

  return (
    <svg className="sk-dashboard-chart" viewBox="0 0 700 190" role="img" aria-label={`Active users by last seen date across ${buckets.length} days. Daily values: ${dailyValues}`} preserveAspectRatio="none">
      <defs><linearGradient id="dashboardAreaFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4de68c" stopOpacity=".42" /><stop offset="1" stopColor="#4de68c" stopOpacity=".03" /></linearGradient></defs>
      {[30, 75, 120, 165].map(y => <line key={y} className="sk-dashboard-gridline" x1="20" y1={y} x2="680" y2={y} />)}
      <path className="sk-dashboard-chart-area" d={area} />
      <path className="sk-dashboard-chart-line" d={line} />
      {points.map((point, index) => (buckets.length <= 7 || index === points.length - 1) && <circle className="sk-dashboard-chart-point" key={point.key} cx={point.x} cy={point.y} r="4" />)}
      {points.map((point, index) => (index % labelEvery === 0 || index === points.length - 1) && <text className="sk-dashboard-axis-label" key={`label-${point.key}`} x={point.x} y="184" textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}>{point.label}</text>)}
    </svg>
  );
};

const relativeTime = value => {
  const elapsed = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return new Date(value).toLocaleString();
  const minutes = Math.floor(elapsed / 60000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr`;
  return `${Math.floor(hours / 24)} d`;
};

export default function Dashboard() {
  const [resources, setResources] = useState({
    users: initialResource,
    logs: initialResource,
    defaultservices: initialResource,
    userservices: initialResource,
    tickets: initialResource,
  });
  const [range, setRange] = useState(7);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [pushMessage, setPushMessage] = useState('');
  const [sendTo, setSendTo] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [mutationLoading, setMutationLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const composerRef = useRef(null);

  const fetchResource = (name, url) => httpClient(url, { method: 'GET' })
    .then(({ json }) => setResources(previous => ({ ...previous, [name]: { data: Array.isArray(json && json.docs) ? json.docs : [], status: 'success', error: null } })))
    .catch(error => {
      console.error(error);
      setResources(previous => ({ ...previous, [name]: { data: [], status: 'error', error: `Could not load ${name}.` } }));
    });

  const fetchLogs = () => fetchAllReportLogs()
    .then(data => setResources(previous => ({ ...previous, logs: { data, status: 'success', error: null } })))
    .catch(error => {
      console.error(error);
      setResources(previous => ({ ...previous, logs: { data: [], status: 'error', error: 'Could not load logs.' } }));
    });

  useEffect(() => {
    fetchResource('users', `${API}/users`);
    fetchLogs();
    fetchResource('defaultservices', `${API}/defaultservices`);
    fetchResource('userservices', `${API}/userservices`);
    fetchResource('tickets', `${API}/tickets`);
    // Each request owns its own state so a fast response cannot hide a slower request.
  }, []);

  const users = resources.users.data;
  const logs = resources.logs.data;
  const reportGroups = useMemo(() => groupReportLogs(logs), [logs]);
  const reportSummary = useMemo(() => summarizeReportGroups(reportGroups), [reportGroups]);
  const analytics = useMemo(() => buildActiveUserAnalytics(users, range), [users, range]);
  const activities = useMemo(() => recentActivity(logs, 4), [logs]);
  const blocks = useMemo(() => summarizeBlocks(users), [users]);
  const countries = useMemo(() => [...new Set(users.map(user => user.country).filter(Boolean))], [users]);
  const cities = useMemo(() => [...new Set(users.map(user => user.city).filter(Boolean))], [users]);
  const activeServices = useMemo(() => countActiveServices(resources.userservices.data), [resources.userservices.data]);
  const openTickets = useMemo(() => countOpenTickets(resources.tickets.data), [resources.tickets.data]);
  const pendingReports = Object.values(reportSummary).reduce((sum, item) => sum + item.open, 0);
  const filteredUsers = users.filter(user => `${user.name || ''} ${user.phone || ''}`.toLowerCase().includes(searchString.toLowerCase()));

  const sendBroadcast = event => {
    event.preventDefault();
    if (!broadcastMessage.trim()) return window.alert('Please enter a message to send.');
    const params = [];
    if (selectedCountry !== 'all') params.push(`country=${encodeURIComponent(selectedCountry)}`);
    if (selectedCity !== 'all') params.push(`city=${encodeURIComponent(selectedCity)}`);
    if (selectedService !== 'all') params.push(`service=${encodeURIComponent(selectedService)}`);
    const query = params.length ? `?${params.join('&')}` : '';
    setMutationLoading(true);
    httpClient(`${API}/notifications/broadcast${query}`, { method: 'POST', body: JSON.stringify({ message: broadcastMessage }) })
      .then(() => { setBroadcastMessage(''); window.alert('Broadcast Message Sent!'); })
      .catch(error => { console.error(error); window.alert('Broadcast message could not be sent.'); })
      .finally(() => setMutationLoading(false));
  };

  const sendPush = () => {
    if (!pushMessage.trim()) return window.alert('Please enter a message to send.');
    if (!sendTo.length) return window.alert('Please select at least one user to send the push notification.');
    setMutationLoading(true);
    httpClient(`${API}/notifications/send`, { method: 'POST', body: JSON.stringify({ message: pushMessage, sendTo }) })
      .then(() => { setPushMessage(''); setSendTo([]); window.alert('Push Message Sent!'); })
      .catch(error => { console.error(error); window.alert('Push message could not be sent.'); })
      .finally(() => setMutationLoading(false));
  };

  const toggleGroup = async (group, targetStatus) => {
    setMutationLoading(true);
    try {
      await Promise.all(group.logs.filter(log => log.status !== targetStatus).map(log => httpClient(`${API}/logs/${log._id}`, {
        method: 'PATCH', body: JSON.stringify({ status: targetStatus }),
      })));
      await fetchLogs();
    } catch (error) {
      console.error(error);
      window.alert('Report status could not be updated.');
    } finally { setMutationLoading(false); }
  };

  const exportReport = () => {
    const rows = [
      ['Metric', 'Value'], ['Total users', users.length], ['Active services', activeServices],
      ['Open tickets', openTickets], ['Pending reports', pendingReports], [`Active users (${range} days, by lastSeen)`, analytics.total],
      ['Request report groups', reportSummary.requests.total], ['Open request report groups', reportSummary.requests.open],
      ['User report groups', reportSummary.users.total], ['Open user report groups', reportSummary.users.open],
      ['Feed report groups', reportSummary.feeds.total], ['Open feed report groups', reportSummary.feeds.open],
      ...analytics.buckets.map(bucket => [`Active users last seen ${bucket.key}`, bucket.value]),
    ];
    const csv = rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url; link.download = `skilz-dashboard-${new Date().toISOString().slice(0, 10)}.csv`; link.click();
    URL.revokeObjectURL(url);
  };

  const trend = analytics.trendPercent === null
    ? `New activity vs preceding ${analytics.comparisonDays} days`
    : `${analytics.trendPercent >= 0 ? '↗' : '↘'} ${Math.abs(analytics.trendPercent)}% vs preceding ${analytics.comparisonDays} days`;

  return (
    <div className="sk-dashboard" id="main-content">
      <section className="sk-dashboard-headline" aria-labelledby="dashboard-title">
        <div><div className="sk-dashboard-eyebrow">Platform overview</div><h1 id="dashboard-title">Dashboard</h1><p>A focused snapshot of platform activity, moderation, and service health.</p></div>
        <div className="sk-dashboard-headline-actions">
          <button className="sk-dashboard-button" type="button" onClick={exportReport}><Icon type="export" />Export report</button>
          <button className="sk-dashboard-button primary" type="button" onClick={() => {
            if (!composerRef.current) return;
            if (typeof composerRef.current.scrollIntoView === 'function') composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            composerRef.current.focus();
          }}><Icon type="send" />New broadcast</button>
        </div>
      </section>

      <section className="sk-dashboard-kpi-grid" aria-label="Key performance indicators">
        <MetricCard title="Total users" route="#/users" tone="green" icon="users" resource={resources.users} value={users.length} detail="user records in the current API response" />
        <MetricCard title="Active services" route="#/userservices" tone="blue" icon="services" resource={resources.userservices} value={activeServices} detail="service records not marked banned" />
        <MetricCard title="Open tickets" route="#/tickets" tone="purple" icon="tickets" resource={resources.tickets} value={openTickets} detail="tickets with an open status" />
        <MetricCard title="Pending reports" route="#/reports" tone="orange" icon="reports" resource={resources.logs} value={pendingReports} detail="open grouped REPORT records" />
      </section>

      <section className="sk-dashboard-main-grid" aria-label="Analytics and activity">
        <Card className="sk-dashboard-panel sk-dashboard-chart-card"><CardContent>
          <SectionHeading title="Active Users Analytics" subtitle="Users bucketed by their latest valid lastSeen calendar date">
            <div className="sk-dashboard-chart-tabs" role="group" aria-label="Analytics date range">{[7, 30, 90].map(days => <button key={days} type="button" aria-pressed={range === days} className={range === days ? 'active' : ''} onClick={() => setRange(days)}>{days} days</button>)}</div>
          </SectionHeading>
          <div className="sk-dashboard-chart-summary"><strong><ResourceValue resource={resources.users}>{number(analytics.total)}</ResourceValue></strong>{resources.users.status === 'success' && <span>{trend}</span>}</div>
          {resources.users.status === 'success'
            ? <div className="sk-dashboard-chart-wrap"><ActiveChart buckets={analytics.buckets} /></div>
            : <div className="sk-dashboard-chart-state" role="status" aria-label={`Active users ${resources.users.status === 'loading' ? 'loading' : 'unavailable'}`}>{resources.users.status === 'loading' ? 'Loading…' : 'Unavailable'}</div>}
        </CardContent></Card>

        <a className="sk-dashboard-card-link sk-dashboard-panel sk-dashboard-activity-card" href="#/logs">
          <SectionHeading title="Recent activity" subtitle="Latest dated events available in REPORT logs" />
          {resources.logs.status === 'loading' && <p className="sk-dashboard-empty">Loading activity…</p>}
          {resources.logs.status === 'error' && <p className="sk-dashboard-empty">Activity is unavailable.</p>}
          {resources.logs.status === 'success' && !activities.length && <p className="sk-dashboard-empty">No dated activity is available.</p>}
          <span className="sk-dashboard-activity-list">{activities.map((activity, index) => <span className="sk-dashboard-activity-row" key={activity.id}><span className="sk-dashboard-activity-person"><span className={`sk-dashboard-mini-avatar tone-${index % 4}`}>{activity.actor.split(/\s+/).map(word => word[0]).join('').slice(0, 2).toUpperCase()}</span><span><strong>{activity.actor}</strong><small>{activity.detail}</small></span></span><time>{relativeTime(activity.createdAt)}</time></span>)}</span>
          <span className="sk-dashboard-view-all">View all activity</span>
        </a>
      </section>

      <section className="sk-dashboard-report-grid" aria-label="Report summaries">
        <ReportCard type="requests" title="Request Reports" route="#/reports/requests" summary={reportSummary.requests} status={resources.logs} />
        <ReportCard type="users" title="User Reports" route="#/reports/users" summary={reportSummary.users} status={resources.logs} />
        <ReportCard type="feeds" title="Feed Reports" route="#/reports/feeds" summary={reportSummary.feeds} status={resources.logs} />
      </section>

      <section className="sk-dashboard-bottom-grid" aria-label="Notification and moderation tools">
        <Card className="sk-dashboard-panel sk-dashboard-notification-card"><CardContent>
          <h2>Broadcast Push Notification</h2><p>Send a push notification through the existing broadcast endpoint, optionally filtered by live user and service data.</p>
          <form className="sk-dashboard-broadcast-form broadcast-composer" onSubmit={sendBroadcast}>
            <label className="broadcast-composer__country"><span className="sk-dashboard-sr-only">Country</span><select aria-label="Broadcast country" disabled={resources.users.status !== 'success'} value={selectedCountry} onChange={event => setSelectedCountry(event.target.value)}><option value="all">All countries</option>{countries.map(country => <option key={country} value={country}>{country}</option>)}</select></label>
            <label className="broadcast-composer__city"><span className="sk-dashboard-sr-only">City</span><select aria-label="Broadcast city" disabled={resources.users.status !== 'success'} value={selectedCity} onChange={event => setSelectedCity(event.target.value)}><option value="all">All cities</option>{cities.map(city => <option key={city} value={city}>{city}</option>)}</select></label>
            {resources.users.status === 'loading' && <p role="status">Loading countries and cities…</p>}
            {resources.users.status === 'error' && <p role="status">Countries and cities unavailable.</p>}
            {resources.users.status === 'success' && countries.length === 0 && cities.length === 0 && <p>No countries or cities are available.</p>}
            <label className="broadcast-composer__audience"><span className="sk-dashboard-sr-only">Audience</span><select aria-label="Broadcast audience" disabled={resources.defaultservices.status !== 'success'} value={selectedService} onChange={event => setSelectedService(event.target.value)}><option value="all">All users</option><option value="with">With a service</option><option value="without">Without a service</option>{resources.defaultservices.data.map(service => <option key={service._id} value={service._id}>{service.name}</option>)}</select></label>
            {resources.defaultservices.status === 'loading' && <p role="status">Loading audience options…</p>}
            {resources.defaultservices.status === 'error' && <p role="status">Audience options unavailable.</p>}
            {resources.defaultservices.status === 'success' && resources.defaultservices.data.length === 0 && <p>No named services are available.</p>}
            <label className="sk-dashboard-message-field broadcast-composer__message"><span className="sk-dashboard-sr-only">Notification message</span><input ref={composerRef} aria-label="Notification message" value={broadcastMessage} onChange={event => setBroadcastMessage(event.target.value)} placeholder="Write a notification message…" /></label>
            <button className="sk-dashboard-button primary broadcast-composer__submit" disabled={mutationLoading || resources.users.status !== 'success' || resources.defaultservices.status !== 'success'} type="submit"><Icon type="send" />Send Push Notification</button>
          </form>
        </CardContent></Card>

        <a className="sk-dashboard-card-link sk-dashboard-panel sk-dashboard-blocks-card" href="#/users">
          <SectionHeading title="Active Blocks" subtitle={resources.users.status === 'loading' ? 'Loading block relationship data' : resources.users.status === 'error' ? 'Block relationship data is unavailable' : blocks.available ? 'Relationships exposed by current user records' : 'Block relationship fields are not available in current user records'} />
          <span className="sk-dashboard-block-list" role="status" aria-label={`Active blocks ${resources.users.status}`}>
            <span className="sk-dashboard-block-row"><span><strong>Active block relationships</strong><small>Entries in users’ blockedUsers arrays</small></span><b>{resources.users.status === 'loading' ? '…' : resources.users.status === 'error' ? 'Unavailable' : blocks.available ? number(blocks.relationships) : '—'}</b></span>
            <span className="sk-dashboard-block-row"><span><strong>Users blocking others</strong><small>Users with at least one active block</small></span><b>{resources.users.status === 'loading' ? '…' : resources.users.status === 'error' ? 'Unavailable' : blocks.available ? number(blocks.blockingUsers) : '—'}</b></span>
            {!blocks.available && resources.users.status === 'success' && <span className="sk-dashboard-empty">No block totals can be derived without inventing data.</span>}
          </span>
        </a>
      </section>

      <section className="sk-dashboard-secondary" aria-label="Additional operational tools">
        <button type="button" aria-expanded={expanded === 'push'} onClick={() => setExpanded(expanded === 'push' ? null : 'push')}>Individual push notifications</button>
        {expanded === 'push' && <Card className="sk-dashboard-panel"><CardContent><h2>Send Push Notification</h2><input aria-label="Search users" disabled={resources.users.status !== 'success'} placeholder="Search by name or phone" value={searchString} onChange={event => setSearchString(event.target.value)} />{resources.users.status === 'loading' && <p role="status">Loading users…</p>}{resources.users.status === 'error' && <p role="status">Users unavailable.</p>}{resources.users.status === 'success' && users.length === 0 && <p>No users are available.</p>}<div className="sk-dashboard-user-picker">{filteredUsers.map(user => <label key={user._id}><input type="checkbox" checked={sendTo.includes(user._id)} onChange={event => setSendTo(previous => event.target.checked ? [...new Set([...previous, user._id])] : previous.filter(id => id !== user._id))} /><span><strong>{user.name || 'Unnamed user'}</strong><small>{user.phone || 'No phone'}</small></span></label>)}</div><div className="sk-dashboard-inline-send"><input aria-label="Individual notification message" value={pushMessage} onChange={event => setPushMessage(event.target.value)} placeholder="Write a message…" /><button className="sk-dashboard-button primary" disabled={mutationLoading || resources.users.status !== 'success'} type="button" onClick={sendPush}>Send</button></div></CardContent></Card>}
        <button type="button" aria-expanded={expanded === 'reports'} onClick={() => setExpanded(expanded === 'reports' ? null : 'reports')}>Report resolution controls</button>
        {expanded === 'reports' && <Card className="sk-dashboard-panel"><CardContent><h2>Report resolution controls</h2>{resources.logs.status === 'loading' && <p role="status">Loading report groups…</p>}{resources.logs.status === 'error' && <p role="status">Report groups unavailable.</p>}{resources.logs.status === 'success' && reportGroups.length === 0 && <p>No report groups are available.</p>}<div className="sk-dashboard-resolution-list">{reportGroups.map(group => <div key={`${group.relatedType}-${group.relatedId}`}><span><strong>{group.relatedType || 'Unknown type'} / {group.relatedId}</strong><small>{group.total} report{group.total === 1 ? '' : 's'} · {group.groupStatus}</small></span>{group.relatedId !== 'UNKNOWN' && <a href={`#/${group.relatedType}/${group.relatedId}/show`}>View details</a>}<button disabled={mutationLoading} type="button" onClick={() => toggleGroup(group, group.groupStatus === 'OPEN' ? 'DONE' : 'OPEN')}>{group.groupStatus === 'OPEN' ? 'Set resolved' : 'Set unresolved'}</button></div>)}</div></CardContent></Card>}
      </section>
    </div>
  );
}
